import { useEffect, useState } from "react";
import { masterApi, apiError } from "@/lib/api";
import { modal } from "@/lib/modal";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MapPin, ExternalLink, ChevronLeft, ChevronRight, Search, Monitor, Smartphone, Tablet, ShieldCheck, ShieldAlert, Globe, RefreshCw, Eye, X, CheckCircle2, XCircle } from "lucide-react";

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

const ACAO_LABEL = { login: "Login", login_falha: "Login (falha)", bloqueado: "Bloqueado", logout: "Logout" };

function dispositivoInfo(ua) {
  const s = (ua || "").toLowerCase();
  if (/ipad|tablet/.test(s)) return { label: "Tablet", Icon: Tablet };
  if (/mobi|android|iphone|ipod/.test(s)) return { label: "Celular", Icon: Smartphone };
  if (!ua) return { label: "—", Icon: Monitor };
  return { label: "Desktop", Icon: Monitor };
}

function navegadorDe(ua) {
  const s = ua || "";
  if (/edg/i.test(s)) return "Edge";
  if (/chrome|crios/i.test(s)) return "Chrome";
  if (/firefox|fxios/i.test(s)) return "Firefox";
  if (/safari/i.test(s)) return "Safari";
  return "—";
}

function fmtData(iso, full = false) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("pt-BR", full
    ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Card({ Icon, bg, color, label, value }) {
  return (
    <div className="card-soft rounded-2xl p-4 flex items-center gap-3">
      <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: bg }}>
        <Icon size={18} style={{ color }} />
      </span>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
        <div className="font-serif-display text-2xl leading-tight">{value}</div>
      </div>
    </div>
  );
}

function Resultado({ log, small }) {
  const ok = log.sucesso !== false && log.acao !== "login_falha" && log.acao !== "bloqueado";
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <span className={`chip ${small ? "text-[9px] py-0.5" : "text-[11px] py-0.5"} inline-flex items-center gap-1`}
          style={ok ? { background: "rgba(16,185,129,0.14)", color: "#047857" }
                    : { background: "rgba(244,63,94,0.12)", color: "#9F1239" }}>
      <Icon size={small ? 11 : 13} /> {ok ? "Sucesso" : "Falha"}
    </span>
  );
}

export default function MasterLogs() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, com_localizacao: 0, sucesso: 0, falha: 0 });
  const [search, setSearch] = useState("");
  const [tipo, setTipo] = useState("");
  const [resultado, setResultado] = useState("");
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);  // log selecionado p/ "Ver detalhes"

  const load = async (targetPage = 1, q = search, t = tipo, r = resultado) => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(targetPage), page_size: "30" });
    if (q.trim()) params.set("search", q.trim());
    if (t) params.set("tipo", t);
    if (r) params.set("resultado", r);
    try {
      const { data } = await masterApi.get(`/master/logs-acesso?${params.toString()}`);
      setItems(data.items || []);
      setPage(data.page || targetPage);
      setTotalPages(data.total_pages || 0);
      setTotal(data.total || 0);
      setStats(data.stats || { total: 0, com_localizacao: 0, sucesso: 0, falha: 0 });
    } catch (e) { modal.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => load(1, search, tipo, resultado), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tipo, resultado]);

  // Esc fecha o modal de detalhes
  useEffect(() => {
    if (!sel) return;
    const onKey = (e) => { if (e.key === "Escape") setSel(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel]);

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-logs">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="chip mb-1.5 text-[10px] py-0.5">Sistema · Auditoria</div>
          <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Logs de Acesso</h1>
          <p className="text-[12px] text-neutral-500 mt-1">Cada tentativa de login: sucesso, falha, IP, dispositivo e localização.</p>
        </div>
        <button onClick={() => load(page)} disabled={loading} data-testid="logs-reload"
                className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Atualizar
        </button>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <Card Icon={ShieldCheck} bg="rgba(10,42,115,0.10)" color="#0A2A73" label="Total de acessos" value={stats.total} />
        <Card Icon={CheckCircle2} bg="rgba(16,185,129,0.12)" color="#047857" label="Sucessos" value={stats.sucesso} />
        <Card Icon={ShieldAlert} bg="rgba(244,63,94,0.12)" color="#9F1239" label="Falhas" value={stats.falha} />
        <Card Icon={Globe} bg="rgba(22,96,214,0.10)" color="#1660D6" label="Com localização" value={stats.com_localizacao} />
      </div>

      {/* Filtros */}
      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Buscar</label>
          <div className="relative mt-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input data-testid="logs-search" value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="usuário, cidade, UF, IP, motivo" className={inputCls + " pl-9"} />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Resultado</label>
          <select value={resultado} onChange={(e) => setResultado(e.target.value)} data-testid="logs-resultado"
                  className={inputCls + " mt-1 w-36"}>
            <option value="">Todos</option>
            <option value="sucesso">Sucesso</option>
            <option value="falha">Falha</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Perfil</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="logs-tipo"
                  className={inputCls + " mt-1 w-36"}>
            <option value="">Todos</option>
            <option value="master">Master</option>
            <option value="frentista">Vendedor</option>
          </select>
        </div>
      </div>

      {/* Tabela */}
      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Usuário</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Resultado</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Quando</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">IP</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Localização</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-right">Detalhes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((log, idx) => {
              const isMaster = log.tipo === "master";
              const temCoord = log.latitude != null && log.longitude != null;
              const temLocal = temCoord || !!log.cidade;
              const porIP = log.fonte === "ip";
              return (
                <TableRow key={idx} data-testid={`log-row-${idx}`}>
                  <TableCell>
                    <div className="font-medium text-sm">{log.usuario || "—"}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="chip text-[9px] py-0.5"
                            style={isMaster
                              ? { background: "rgba(251,191,36,0.16)", color: "#92400E" }
                              : { background: "rgba(22,96,214,0.10)", color: "#0A2A73" }}>
                        {isMaster ? "Master" : "Vendedor"}
                      </span>
                      {log.unit_nome ? <span className="text-[11px] text-neutral-400">{log.unit_nome}</span> : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Resultado log={log} />
                    {log.detalhe ? <div className="text-[11px] text-neutral-400 mt-0.5 max-w-[160px] truncate" title={log.detalhe}>{log.detalhe}</div> : null}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{fmtData(log.criado_em)}</TableCell>
                  <TableCell className="text-xs font-mono-ibm">{log.ip || "—"}</TableCell>
                  <TableCell>
                    {temLocal ? (
                      <div className="flex items-start gap-1.5">
                        <MapPin size={13} className={`mt-0.5 shrink-0 ${porIP ? "text-amber-500" : "text-emerald-600"}`} />
                        <div className="leading-tight min-w-0">
                          <div className="flex items-center gap-1.5">
                            {temCoord ? (
                              <a href={`https://www.google.com/maps?q=${log.latitude},${log.longitude}`}
                                 target="_blank" rel="noreferrer" data-testid={`log-map-${idx}`}
                                 className="text-xs font-medium text-[color:var(--brand-blue)] hover:underline inline-flex items-center gap-1">
                                {[log.cidade, log.estado].filter(Boolean).join(" - ") || `${log.latitude.toFixed(4)}, ${log.longitude.toFixed(4)}`}
                                <ExternalLink size={10} />
                              </a>
                            ) : (
                              <span className="text-xs font-medium text-neutral-800">{[log.cidade, log.estado].filter(Boolean).join(" - ") || "—"}</span>
                            )}
                            <span className="chip text-[8px] py-0 px-1.5"
                                  style={porIP ? { background: "rgba(245,158,11,0.14)", color: "#92400E" }
                                               : { background: "rgba(16,185,129,0.12)", color: "#047857" }}>
                              {porIP ? "≈ IP" : "GPS"}
                            </span>
                          </div>
                          {(log.bairro || log.endereco) && (
                            <div className="text-[11px] text-neutral-400 max-w-[260px] truncate">
                              {[log.bairro, log.endereco].filter(Boolean).join(" · ")}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : <span className="text-xs text-neutral-400">Sem localização</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => setSel(log)} data-testid={`log-detalhes-${idx}`}
                            className="btn-ghost h-8 px-2.5 text-[11px] font-medium inline-flex items-center gap-1.5">
                      <Eye size={13} /> Ver
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-8">
                {loading ? "Carregando..." : "Nenhum acesso registrado."}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <button data-testid="logs-prev" disabled={page <= 1} onClick={() => load(page - 1)}
                  className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-40">
            <ChevronLeft size={13} /> Anterior
          </button>
          <span className="text-[11px] text-neutral-600">Página <b>{page}</b> de <b>{totalPages}</b></span>
          <button data-testid="logs-next" disabled={page >= totalPages} onClick={() => load(page + 1)}
                  className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-40">
            Próxima <ChevronRight size={13} />
          </button>
        </div>
      )}

      {sel && <DetalhesModal log={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

function Linha({ label, value, mono }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-[var(--line)] last:border-0">
      <span className="text-[10px] uppercase tracking-widest text-neutral-500 w-28 shrink-0 pt-0.5">{label}</span>
      <span className={`text-sm text-neutral-800 break-words min-w-0 ${mono ? "font-mono-ibm text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function DetalhesModal({ log, onClose }) {
  const dev = dispositivoInfo(log.user_agent);
  const isMaster = log.tipo === "master";
  const temCoord = log.latitude != null && log.longitude != null;
  const loc = [log.cidade, log.estado].filter(Boolean).join(" - ");
  const endereco = [log.bairro, log.endereco].filter(Boolean).join(" · ");
  const porIP = log.fonte === "ip";

  return (
    <div className="modal-overlay" role="presentation"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-card" role="dialog" aria-modal="true"
           style={{ maxWidth: 520, textAlign: "left", padding: "22px 22px 18px" }}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="font-serif-display text-xl leading-tight">{log.usuario || "—"}</div>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="chip text-[9px] py-0.5"
                    style={isMaster ? { background: "rgba(251,191,36,0.16)", color: "#92400E" }
                                    : { background: "rgba(22,96,214,0.10)", color: "#0A2A73" }}>
                {isMaster ? "Master" : "Vendedor"}
              </span>
              <Resultado log={log} small />
            </div>
          </div>
          <button onClick={onClose} data-testid="detalhes-fechar"
                  className="btn-ghost h-8 w-8 p-0 flex items-center justify-center shrink-0"><X size={15} /></button>
        </div>

        <div className="rounded-xl border border-[var(--line)] bg-[#F8FAFC] px-3.5 py-1">
          <Linha label="Ação" value={ACAO_LABEL[log.acao] || log.acao || "login"} />
          {log.detalhe ? <Linha label="Motivo" value={log.detalhe} /> : null}
          <Linha label="Quando" value={fmtData(log.criado_em, true)} />
          <Linha label="IP" value={log.ip} mono />
          <Linha label="Dispositivo" value={`${dev.label} · ${navegadorDe(log.user_agent)}`} />
          <Linha label="Localização" value={loc || (temCoord ? `${log.latitude}, ${log.longitude}` : "Sem localização")} />
          {endereco ? <Linha label="Endereço" value={endereco} /> : null}
          {temCoord ? <Linha label="Coordenadas" value={`${log.latitude}, ${log.longitude}`} mono /> : null}
          <Linha label="Fonte" value={porIP ? "Aproximada (IP)" : (log.fonte === "gps" ? "GPS (precisa)" : "—")} />
          {log.unit_nome ? <Linha label="Unidade" value={log.unit_nome} /> : null}
          <Linha label="User-Agent" value={log.user_agent} mono />
        </div>

        <div className="flex justify-end gap-2 mt-4">
          {temCoord && (
            <a href={`https://www.google.com/maps?q=${log.latitude},${log.longitude}`} target="_blank" rel="noreferrer"
               className="btn-action btn-salvar modal-btn inline-flex items-center gap-1.5">
              <ExternalLink size={14} /> Abrir no mapa
            </a>
          )}
          <button onClick={onClose} className="btn-ghost modal-btn">Fechar</button>
        </div>
      </div>
    </div>
  );
}
